<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-flows">
    <div class="wy-flows-filter">
      <table border="0">
      <tr>
        <td width="*">
          <div class="wy-small-title">
            {{$t(currentPageInfo.subtitle)}}
          </div>
          <div class="wy-title">
            Intent Flows
            <el-tooltip effet="dark">
              <div slot="content">Presents a visual flow of traffic and studies customer behavior to identify opportunities for journey improvement. <br/>Ex. journey with 3 buttons, but customer doesn’t use<br/><br/> - Ability to view specific intents and messages to understand what the actual customer paths were<br/> - Identify where positive/negative feedback occurred in a journey<br/>- Provides a ‘tree view’ of the customer journey through the intents</div>
              <img class="intent-flow-info-icon" src="../../assets/icons/icon_response_page_info.svg" alt="">
            </el-tooltip>
          </div>
        </td>
      </tr>
      </table>
    </div>

    <div v-if="intentList && intentList.length > 0">
      <div class="wy-flows-searchKeyword" v-show="selectedIntent && selectedIntent != 'Flow Starting point'">
        {{selectedIntent}}
      </div>
      <div :class="{'wy-flow-conteiner': true, 'isFullScreen': isFullScreen}" v-loading="loading" element-loading-background="transparent">

      <div style="display: flex; align-items: center;">
          <el-select class="wy-flows-search" v-model="selectedIntent" filterable placeholder="Select an Intent" style="width: 280px;" @change="handleSearch">
            <el-option
              v-for="intent in intentList"
              :key="intent.label"
              :label="intent.label"
              :value="intent.label">
            </el-option>
          </el-select>
        
          <el-popover
            placement="bottom"
            title="Configure"
            width="340"
            ref="tablePopover"
            popper-class="wy-flows-config-popup"
            trigger="click">
            <div class="popup-content">
              <table width="100%">
              <tr>
                <td>Intents displayed</td>
                <td align="right">
                  <el-select ref="flowDropdownItem" v-model="options.maxVisibleNodes" size="small" popper-class="wy-flows-dropdown" style="width: 90px;" @change="handleUpdateTree">
                    <el-option
                      v-for="index in 99" :key="index"
                      :label="`${index} ${index==1 ? 'item' : 'items'}`"
                      :value="index">
                    </el-option>
                  </el-select>
                </td>
              </tr>
              <tr>
                <td>“Other” intents expands by</td>
                <td align="right">
                  <el-select @change="handlePopoverHide" ref="flowDropdownExpand" v-model="options.itemsPerExpand" size="small" popper-class="wy-flows-dropdown" style="width: 90px;">
                    <el-option
                      v-for="index in 20" :key="index"
                      :label="`${index} ${index==1 ? 'item' : 'items'}`"
                      :value="index">
                    </el-option>
                  </el-select>
                </td>
              </tr>
              </table>
            </div>
            <div slot="reference" class="wy-flows-config-gear" :style="`background-image: url('${this.$root.locationPath}/images/flows/icon_tree_gear.svg');`"/>
          </el-popover>          
        </div>

        <div v-if="hasData" class="wy-flow-tree-chart">
          <div id="chart"/>
        </div>
        <span v-else>
          <div class="no-result-found">
            <div class="no-data-icon"></div>
            <div class="no-data-title">Sorry!</div>
            <div class="no-data-content">
              <div class="content-text">
                No data available. Try adjusting your date range or flow starting point to find the information you’re looking for. 
              </div>
            </div>
          </div>
        </span>
        <div class="wy-flow-tree-transcript">
          <wy-flow-messages ref="wyFlowMessages" />
        </div>
        <div style="clear: both" />
      </div>
      <div class="floating-menu">
        <div class="zoom-in floating-menu-icon" :style="`background-image: url('${this.$root.locationPath}/images/flows/icon_zoom_in.svg');`" @click="handleZoomTree(1)"/>
        <div class="zoom-out floating-menu-icon" :style="`background-image: url('${this.$root.locationPath}/images/flows/icon_zoom_out.svg');`" @click="handleZoomTree(0)"/>
        <div class="zoom-reset floating-menu-icon" :style="`background-image: url('${this.$root.locationPath}/images/flows/icon_reset.svg');`" @click="handleResetTree()"/>
      </div>
    </div>
    <div v-else class="no-result-found">
      <div class="no-data"></div>
      <div class="no-data-title">{{$t('noResultFound.title')}}</div>
      <div class="no-data-content">
        <div class="content-text">
          {{$t('noResultFound.message')}}
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import treeModule from './wy-flow-tree';
import WyFlowMessages from './wy-flow-messages';
import WyUtils from '../../library/wy-utils.js';
import { uuid } from 'vue-uuid';

export default Vue.extend({
  name: 'wy-flows',  
  components: {
    'wy-flow-messages': WyFlowMessages
  },

  data() {
    return {
      initDate: null,
      hasData: true,
      intentList: null,
      selectedIntent: 'Flow Starting point',
      defaultPath: '||Start||',
      searchPath: null,
      loading: false,
      preMsgObj: null,
      isFullScreen: true,
      options: {
        maxVisibleNodes: 3,
        itemsPerExpand: 3,
        nodeHeight: 30,
        margin: 20,
      },
      specialNode: {
        '$positive_feedback': {
          type: 'feedback',
          subtype: 'positive-feedback',
          order: 2,
          label: 'Positive feedback',
          image: 'icon_tree_feeback_up.svg'
        },
        '$negative_feedback': {
          type: 'feedback',
          subtype: 'negative-feedback',
          order: 2,
          label: 'Negative feedback',
          image: 'icon_tree_feeback_down.svg'
        },
        '$not_handled': {
          type: 'not_handled',
          order: 3,
          label: 'Not understood',
          isCollapsed: true,
          image: 'icon_tree_not_handled.svg'
        },
        'others': {
          type: 'others',
          order: 1,
          label: 'Others',
          image: 'icon_tree_others.svg'
        },
        'end': {
          type: 'end',
          order: 1,
          label: 'End',
          image: 'icon_tree_end.svg'
        },
        '$escalation_requested': {
          type: 'spec',
          order: 3,
          label: 'Live agent requested but not connected'
        },
        '$bot_quit': {
          type: 'spec',
          order: 3,
          label: 'Bot quit'
        }
      },

      rootNode: {
        uuid: 'Root',
        type: 'root',
        intent: "Flow Starting point",
        depth: 1,
        labelWidth: 60,
        maxLabelWidth: 60,
        children: []
      },

      filters: {
        'channel': {label: 'Channel', name: 'channel', value: 0, type: "select", dataSourceName: "channels"}
      },
    }
  },

  watch: {
    'loading': function(val) {
      setTimeout(()=>{
        val ? this.showTable = false : this.showTable = true
      }, 300)
    },
  },

  computed: {
    ...mapGetters(['config', 'currentPageInfo', 'dataModel', 'isLoggedIn']),
  },
  
  methods: {
    ...mapActions(['fetchFlowsIntent', 'fetchFlowsData']),

    async handleFilterChange() {

      if((!this.loading && this.dataModel.dateEnd) || this.dataModel.dateEnd) {
        this.loading = true;
        let res = await this.fetchFlowsIntent();
        if(res.status == 200 && res && res.data) {
          this.intentList = res.data;
          if(this.intentList && this.intentList.length > 0) {
            this.intentList.unshift({label: 'Flow Starting point'});
          }
        }
        this.selectedIntent = 'Flow Starting point';
        await this.buildChart(this.defaultPath);
      }
    },
    async fetchData(parent, searchPath, isFetchChildren) {
      return new Promise(async (resolve) => {
        this.loading = true;
        let res = await this.fetchFlowsData(searchPath);
        if(res.status == 200 && res && res.data && res.data.length > 0) {
          this.hasData = true;

          // init items
          let items = res.data;
          items.forEach(node => {
            node.uuid = uuid.v4();
            node.key = node.label;
            node.type = 'regular';
            node.depth = parent.depth + 1;
            node.order = 0;
            node.children = [];
            node.maxVisibleNodes = this.options.maxVisibleNodes;
            node.regularNodesCount = 0;

            let labelWidth = WyUtils.textWidth(node.label, '500 12px "Helvetica Neue", Helvetica, Arial, sans-serif') * 1.01;
            let labelHoverWidth = WyUtils.textWidth(node.label, '500 13px "Helvetica Neue", Helvetica, Arial, sans-serif') * 1.01;
            let labelHoverDiff = labelHoverWidth - labelWidth;
            node.labelWidth = labelWidth + 20;
            node.labelHoverWidth = labelHoverWidth;
            node.labelHoverDiff = labelHoverDiff;

            if(this.specialNode[node.label]) {
              node.type = this.specialNode[node.label].type;
              node.subtype = this.specialNode[node.label].subtype;
              node.order = this.specialNode[node.label].order;
              node.image = this.specialNode[node.label].image;            
              node.label = this.specialNode[node.label].label;

              let labelWidth = WyUtils.textWidth(node.label, '500 12px "Helvetica Neue", Helvetica, Arial, sans-serif') * 1.01;
              let labelHoverWidth = WyUtils.textWidth(node.label, '500 13px "Helvetica Neue", Helvetica, Arial, sans-serif') * 1.01;
              let labelHoverDiff = labelHoverWidth - labelWidth;
              node.labelWidth = labelWidth + 20;
              node.labelHoverWidth = labelHoverWidth;
              node.labelHoverDiff = labelHoverDiff;
            }
          });
          parent.children = items;

          // sort by percent
          parent.children.sort(function(a, b) {
            if (a.countPercent < b.countPercent) return 1;
            else if (a.countPercent > b.countPercent) return -1;
            else return 0;
          });

          // group items
          let regularNodes = parent.children.filter(el => el.type==='regular');
          parent.regularNodesCount = regularNodes.length;        
          if(parent.regularNodesCount > parent.maxVisibleNodes) {
            let otherNode = {
              uuid: uuid.v4(),
              type: this.specialNode['others'].type,
              order: this.specialNode['others'].order,
              label: this.specialNode['others'].label,
              image: this.specialNode['others'].image,
              count: 0,
              countPercent: 0,
              children: [],
              maxVisibleNodes: this.options.maxVisibleNodes,
              regularNodesCount: 0,            
              depth: parent.depth + 1,
              intent: 'others',
              labelWidth: 90,
              labelHoverDiff: 6,
              isOther: true,
            };
            parent.children.push(otherNode);
          }

          // set items
          let maxLabelWidth = parent.children.reduce(function(max, x) { return (x.labelWidth > max) ? x.labelWidth : max; }, 0);
          let totalCount = parent.children.reduce((total, item) => { return total+item.count }, 0);
          parent.children.forEach(item => {
            item.maxLabelWidth = maxLabelWidth;
            item.countPercent = item.count/totalCount;                
          })

          // sort by type
          parent.children.sort(function(a, b) {
              if (a.order < b.order) return -1;
              else if (a.order > b.order) return 1;
              else return 0;
          });
        }
        else {
          if(!isFetchChildren) {
            this.hasData = false;
          }
          
          parent.isEndNode = true;
          parent.children = [];
          let endNode = {
            uuid: uuid.v4(),
            type: this.specialNode['end'].type,
            order: this.specialNode['end'].order,
            label: this.specialNode['end'].label,
            image: this.specialNode['end'].image,
            count: 0,
            countPercent: 0,
            children: [],
            maxVisibleNodes: this.options.maxVisibleNodes,
            regularNodesCount: 0,            
            depth: parent.depth + 1,
            intent: 'end',
            labelWidth: 48,
            labelHoverDiff: 0,
            isEndNode: true,
          };
          parent.children.push(endNode);
        }
        this.loading = false;

        resolve(true);
      });
    },

    async fetchChildren(node, searchPath, callback) {
      
      if(!this.rootNode.isRootSearch) {
        // handle intent search without "start"
        const searchPathArr = searchPath.split("||");
        if(searchPathArr.length > 1) {
          searchPath = "||" + searchPathArr.slice((node.depth+1) * -1).join("||");
        }
      }

      await this.fetchData(node, searchPath, true);
      node.messageData = await this.$refs.wyFlowMessages.fetchData(searchPath);
      callback(node.children);
    },

    loadNodeMessages(node, searchPath) {
      this.$refs.wyFlowMessages.loadMessages(node.messageData, searchPath);
    },

    showMessage() {
      if(this.preMsgObj == null){
        this.preMsgObj = this.$message({
          dangerouslyUseHTMLString: false,
          duration: 2000,
          showClose: true,
          customClass: 'nodata-warning-msg',
          message: 'No more nodes.',
          onClose: ()=>{
            this.preMsgObj = null;
          }
        });
      }
    },

    handleZoomTree(type) {
      treeModule.zoomTree(type);
    },

    handleResetTree() {
      treeModule.resetTree();
    },

    async handleReloadTree() {
      await this.buildChart(this.searchPath || this.defaultPath);
    },

    async handleUpdateTree() {
      this.handlePopoverHide()
      await this.buildChart(this.searchPath || this.defaultPath);
    },
    handlePopoverHide(){
      this.$refs.tablePopover.doClose();
    },

    async handleSearch() {
      if(this.selectedIntent == 'Flow Starting point' && this.selectedIntent && this.selectedIntent != ''){
        await this.buildChart('||Start||');
      }
      else if(this.selectedIntent && this.selectedIntent != '') {
        await this.buildChart('||'+this.selectedIntent+'||');
      }
      else {
        await this.buildChart(this.defaultPath);
      }      
    },

    async buildChart(searchPath) {
      this.rootNode.label = this.selectedIntent;
      this.rootNode.maxVisibleNodes = this.options.maxVisibleNodes;
      this.rootNode.children = [];
      this.rootNode.isRootSearch = searchPath==this.defaultPath;
      await this.fetchData(this.rootNode, searchPath);

      if(this.hasData) {
        let flowChartDiv = document.querySelector('.wy-flow-tree-chart');
        if(flowChartDiv) {
          var divWidth = flowChartDiv.clientWidth;
          var divHeight = flowChartDiv.clientHeight;
          treeModule.init("#chart", divWidth, divHeight, this.options, this.$root.locationPath, this, this.$store.getters.xnumber);
        }
        treeModule.updateTree(this.rootNode);
      }
    },

    hideDropdowns() {
      if(this.$refs.flowDropdownItem) this.$refs.flowDropdownItem.blur();
      if(this.$refs.flowDropdownExpand) this.$refs.flowDropdownExpand.blur();
    }
  },

  async created() {
    this.initDate = new Date();
    // listen events from other components
    this.$root.registerPageEvent(this.$route.name, (event) => {
      if(this[event.name]) this[event.name]();
    })

    await this.handleFilterChange();
  },

  async mounted() {
    document.body.addEventListener('click', this.hideDropdowns);
  },

  beforeDestroy() {
    treeModule.destroy();
    document.body.removeEventListener('click', this.hideDropdowns);
  },

  destroyed() {
    if(this.isLoggedIn) {
      this.$store.dispatch('amplitudeLogWithFilters', {name: 'optimization_intent_flows_time_spent', data: {time_spent: WyUtils.getTimeDiff(this.initDate)}});
    }
  }
});
</script>

<style lang="scss">
.wy-flows {
  box-sizing: content-box;
  .no-data{
    height: 163px !important;
    background-image: url('../../assets/icons/icon_no_data_signal.svg') !important;
  }
  .no-data-icon{
    text-align: center;
    display: block;
    border: none;
    height: 163px;
    margin-top: 0%;
    background-position-x: 50%;
    background-repeat: no-repeat;
    background-image: url('../../assets/icons/icon_no_data_signal.svg');
  }

  .no-result-found .no-data-content .content-text {
    width: 378px;
    margin-top: 20px;
    color: #4B5158;
    font-size: 14.5px;
  }
  .no-result-found .no-data-title {
    font-size: 21px;
    color: var(--site-font-tertiary-color)!important;
  }
  .wy-flows-search {
    .el-input__inner {
      border-radius: 20px;
      border: 1px solid #D7D7D8 !important;
      padding-left: 16px;
    }
  }

  img.intent-flow-info-icon {
    width: 16px;
    position: relative;
    top: 0px;
  }
  .wy-flows-config-gear {
    width: 20px;
    height: 20px;
    cursor: pointer;
    background-size: 20px 20px;
    margin-left: 11px;
    background-repeat: no-repeat;
  }
  .zoom-in.floating-menu-icon, .zoom-out.floating-menu-icon {
    width: 29.57px !important;
    position: relative;
    left: 2px;
    height: 29.57px !important;
    background-size: 92% !important;
  }
  .zoom-reset.floating-menu-icon {
    width: 23.67px !important;
    height: 23.67px !important;
    background-size: 92% !important;
    background-position-x: 2.5px !important;
  }
  .wy-flows-dropdown {
    .el-select-dropdown__item.selected {
      color: #4C7CAE;
    }
  }

  .el-loading-mask {
    height: auto !important;
  }

  #chart {
    > svg {
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      width: calc(100% - 2px);
      // height: calc(100% + 19px);
      height: calc(100% + 0px);
    }
  }

  .tree-icon {    
    position: relative;
    top: 5px;
    font-size: 24px;
    cursor: pointer;
  }
  .volume_box {
    text-align: right;
    height: 38px;
    padding-right: 5px;

    .volume_button {
      display: inline-block;
      width: 24px;
      height: 24px;
      height: 100%;
      cursor: pointer;
      background-image: url('../../assets/icons/volume_on.png');
      background-size: 24px 24px;
      background-repeat: no-repeat;
      background-position-y: bottom;
    }

    .volume_button.off {
      background-image: url('../../assets/icons/volume_off.png');
    }
  }

  .wy-flows-filter {
    margin-bottom: 0px;
    // border-bottom: 1px solid #EBEBEB;
    padding-bottom: 20px;
    //padding-left: 22px;
    padding-right: 9px;
    margin-top: 0px;

    .wy-small-title {
      display: none;
      color: var(--site-font-secondary-color);
      font-size: 15px;
      margin-left: 28px;
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      font-weight: 400;
    }
    .wy-title {
      color: var(--site-font-secondary-color);
      font-size: 25px;
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      font-weight: 600;
      padding-top: 0px;
      padding: 0px 23px 0px 18px;
      height: 24px;
      line-height: 0.7;
    }

    .el-select {
      width: 400px;
    }
      .wy-flows-search {
        .el-input__inner {
          border-radius: 20px;
          border: 1px solid #D7D7D8 !important;
          padding-left: 16px;
        }
      }

      .wy-flows-config-gear {
        width: 20px;
        height: 20px;
        cursor: pointer;      
        background-size: 20px 20px;
        background-repeat: no-repeat;
      }
    > table {
      width: 100%;
      border-spacing: 0px;
    }
  }

  .wy-flows-searchKeyword {
    color: #4C7CAE;
    padding-top: 5px;
    margin-left: 27px !important;
    height: 18px;
  }

  .wy-flow-conteiner {
    // height: calc(100vh - 190px);
    height: calc(100vh - 240px);
    // padding-bottom: 52px !important;
    background: white;
    width: calc(100% - 80px);
    // min-width: 1463px;
    border: 1px solid #EBEBEB;
    border-radius: 5px;
    margin: 15px 20px 20px 20px;
    padding: 20px;
    .no-result-found {
      margin-top: 8% !important;
      width: 75% !important;
      display: inline-block;
    }
  }

  // .wy-flow-conteiner.isFullScreen {
  //   .wy-flow-tree-chart {
  //     width: calc(100% - 30px);
  //     svg {
  //       width: calc(100% - 30px);
  //     }
  //   }
  //   .wy-flow-tree-transcript {
  //     display: none;
  //   }
  // }

  .wy-flow-tree-chart {
    float: left;
    width: calc(100% - 380px);
    //border: 1px solid red;
    box-sizing: content-box;
    cursor: pointer !important;
    div#chart {
      height: calc(100vh - 270px);  
    }

    // root node
    g.node.type-root {
      cursor: pointer;
      rect.node-rect {
        fill: none;
        height: 60px;
        opacity: 1.0 !important;
      }
      image {
        width: 60px;
        height: 60px;        
      }
      rect.node-percent {
        // width: 0px;
        // height: 0px;
        x: 6;
        y: -60;
      }
      text.node-percent-text {
        transform: translate(60px, -50px);
      }
    }
    .selectedNode text.node-text {
      font-weight: 700 !important;
      fill: #fff;
      cursor: pointer;
    }
    // normal node
    g.node {
      rect.node-rect {
        height: 30px;
        fill: #8E88DC;
        stroke-width: 0px;
        cursor: pointer;
      }
      
      text.node-text {
        font: 12px "Helvetica Neue", Helvetica, Arial, sans-serif;
        font-weight: 500;
        fill: #fff;
        cursor: pointer;
      }

      image.collapse-image, image.expand-image {
        cursor: pointer;
      }
      
      rect.node-percent {
        fill: #E4E8EC;
        opacity: 0.9;
        cursor: default;
      }
      text.node-percent-text {
        font: 10px "Helvetica Neue", Helvetica, Arial, sans-serif;
        fill: var(--site-font-secondary-color);
        cursor: default;
      }
    }

    g.node.selectedNode {
      rect.node-rect {
        filter: url(#node-selected-shadow);
        stroke: #fff;
        stroke-width: 4px;
      }
    }

    g.node.active {
      rect.node-rect {
        filter: url(#node-hover-shadow) !important;        
        stroke: none;
        stroke-width: 0;
      }
      text.node-text {
        font: 12px "Helvetica Neue", Helvetica, Arial, sans-serif;
        font-weight: 700;
      }
    }

    // g.node.children, g.node.type-others {
    //   rect.node-rect {
    //     cursor: pointer;
    //   }
    //   text.node-text {
    //     cursor: pointer;
    //   }
    // }

    g.node.type-others.collapsed  {
      rect.node-rect {
        width: 96px !important;
      }
      text.node-text {
        fill: #000;
      }
    }
    g.node.type-others.collapsed.active  {
      rect.node-rect {
        opacity: 0.8 !important;
        fill: #CCE6FF;
        filter: none !important;
      }
    }

    g.node.type-not-handled  {
      rect.node-rect {
        fill: #7A91A3 !important;
      }
    }

    g.node.type-feedback, g.node.type-negative-feedback  {
      rect.node-rect {
        fill: #4B67A3 !important;
      }
    }

    g.node.type-feedback.positive-feedback {
      rect.node-rect {
        fill: #DDEDE0 !important;
      }
      text.node-text {
        fill: #29592F;
      }
    } 

    g.node.type-feedback.negative-feedback {
      rect.node-rect {
        fill: #FDEEEE !important;
      }
      text.node-text {
        fill: #B53655;
      }
    }

    g.node.type-end  {
      cursor: default;
      rect.node-rect {
        cursor: default;
        fill: #8AA8D1 !important;        
      }
      text.node-text {
        cursor: default;
      }
    }

    g.node.type-spec  {
      rect.node-rect {
        fill: #4B67A3 !important;        
      }
      text.node-text {
        fill: #FFFFFF;
      }
    }
    
    path.link {
      fill: none;
      stroke: #ccc;
      opacity: 0.4;
    }

    path.link.active {
      stroke: #106FBC;
      opacity: 1.0;
    }

    // tooltip
    div.tooltip {
      position: absolute;			
      text-align: left;    
      padding: 16px 16px 2px 16px;
      font: 12px "Helvetica Neue", Helvetica, Arial, sans-serif;
      background: #fff;
      opacity: 0.98;
      border: 0px solid #ffcf9e;		
      border-radius: 10px;
      pointer-events: none;
      box-shadow: 3px 3px 1px #00000021;

      .title {
        font-weight: bold;
        font-size: 13px;
        margin-bottom: 12px;
      }
      .row {
        margin-bottom: 9px;
        .label {
          color: #666;
          font-size: 13px;
          margin-bottom: 4px;
        }
        .value {
          font-size: 18px;
          color: var(--site-font-primary-color);
        }
      }
    }
  }

  .floating-menu {
    display: inline-block;
    position: absolute;
    background-color: #fff;
    opacity: 0.9;
    right: 445px;
    bottom: 40px;
    padding: 8px;
    background: #FFFFFF;
    box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.2), 0px 2px 10px rgba(0, 0, 0, 0.1);
    border-radius: 5px;

    .floating-menu-icon {
      background-color: #fff;
      width: 24px;
      height: 24px;
      margin-bottom: 5px;
      background-size: 24px 24px;
      background-repeat: no-repeat;
      background-position-y: bottom;
      cursor: pointer;
    }

    .floating-menu-icon:last-child {
      margin-bottom: 0px;
    }
  }

  .wy-flow-tree-transcript {
    width: 350px;
    height: 100%;
    margin-right: 15px;
    float: right;
    box-sizing: border-box;
  }
}

.wy-flows-config-popup {
  padding: 0px;
  box-shadow: 0px 1px 2px #0000002E;
  border: 1px solid #F5F5F5;
  border-radius: 10px;
  
  .el-popover__title {
    border-bottom: 1px solid #F8F9FA;
    margin-bottom: 8px;
    padding: 18px 18px 10px 18px;
    color: #4C7CAE;
  }
  .popup-content {
    padding: 0 18px 18px 18px;
  }
}
</style>